import api from './api';

export type ReportColumnType = 'text' | 'code' | 'date' | 'money' | 'number' | 'percent' | 'status';

export interface ReportColumn {
  key: string;
  label: string;
  type: ReportColumnType;
  // Added up in the totals row
  total?: boolean;
}

export interface ReportOption {
  value: string;
  label: string;
}

export interface ReportSummary {
  key: string;
  name: string;
  category: string;
  description: string;
  columns: ReportColumn[];
  // What the period applies to; null when the report shows the position as of now
  periodLabel: string | null;
  statusLabel: string | null;
  statuses: ReportOption[];
  searchable: boolean;
  defaultSort: { column: string; direction: 'ASC' | 'DESC' };
}

export type ReportRow = Record<string, string | number | null>;

export interface ReportResult {
  report: ReportSummary;
  rows: ReportRow[];
  // Sums over every row of the report, not just the page shown
  totals: Record<string, number>;
  total: number;
  page: number;
  limit: number;
  totalPages: number;
  sortBy: string;
  sortOrder: 'ASC' | 'DESC';
  generatedAt: string;
}

export interface ReportFilters {
  fromDate?: string;
  toDate?: string;
  status?: string;
  search?: string;
  sortBy?: string;
  sortOrder?: 'ASC' | 'DESC';
}

export interface ReportQueryParams extends ReportFilters {
  page?: number;
  limit?: number;
}

// Empty filters are left out of the request
const clean = (params: object) =>
  Object.fromEntries(Object.entries(params).filter(([, value]) => value !== undefined && value !== ''));

export const reportsService = {
  getCatalogue: async (): Promise<ReportSummary[]> => {
    const response = await api.get('/reports');
    return response.data;
  },

  runReport: async (key: string, params: ReportQueryParams): Promise<ReportResult> => {
    const response = await api.get(`/reports/${key}`, { params: clean(params) });
    return response.data;
  },

  // The whole report, with the filters on screen, as a CSV file
  exportReport: async (key: string, filters: ReportFilters): Promise<Blob> => {
    const response = await api.get(`/reports/${key}/export`, { params: clean(filters), responseType: 'blob' });
    return response.data;
  },
};
